import { useEffect, useRef } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useMatches } from 'react-router'
import { OfflineBanner } from '../components/OfflineBanner'
import { ToastViewport } from '../components/Toast'
import { useSession } from '../session/useSession'
import { BrandMark } from './BrandMark'
import { PaletteSwitcher, ThemeSwitcher } from './ThemeSwitcher'
import './AppShell.css'

/** Route `handle` fields the shell reads. */
export interface RouteHandle {
  /** Replaces the general offline message on this screen (e.g. "The calculator still works"). */
  offlineMessage?: (t: TFunction) => string
}

/**
 * The public shell: skip link, header, offline banner, main content, footer and the
 * toast region. Signed-in screens under /app use SignedInShell instead (bottom bar per
 * role, side rail on tablets, the console's side navigation at 1024 px and up).
 */
export function AppShell() {
  const { t } = useTranslation()
  const { status } = useSession()
  const location = useLocation()
  const matches = useMatches()
  const mainRef = useRef<HTMLElement>(null)
  const shownPath = useRef(location.pathname)

  // After navigating, move focus to the new screen so screen readers start there.
  useEffect(() => {
    if (shownPath.current === location.pathname) return
    shownPath.current = location.pathname
    mainRef.current?.focus({ preventScroll: true })
  }, [location.pathname])

  const handle = [...matches].reverse().find((match) => (match.handle as RouteHandle | undefined)?.offlineMessage)
    ?.handle as RouteHandle | undefined

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>
      <header className="app-header">
        <div className="container container--wide app-header__inner">
          <Link to="/" className="brand" aria-label={t('app.homeLink')}>
            <BrandMark />
            <span className="brand__name" aria-hidden="true">
              {t('app.name')}
            </span>
          </Link>
          <nav aria-label={t('app.primaryNav')} className="app-nav">
            <NavLink to="/calculator" className="app-nav__link">
              {t('app.nav.calculator')}
            </NavLink>
            {status === 'signed-in' ? (
              <NavLink to="/app" className="app-nav__link app-nav__link--cta">
                {t('app.nav.openApp')}
              </NavLink>
            ) : (
              <NavLink to="/signin" className="app-nav__link app-nav__link--cta">
                {t('app.nav.signIn')}
              </NavLink>
            )}
          </nav>
        </div>
      </header>
      <OfflineBanner message={handle?.offlineMessage?.(t)} />
      <main id="main" ref={mainRef} tabIndex={-1} className="app-main">
        <Outlet />
      </main>
      <footer className="app-footer">
        <div className="container container--wide app-footer__inner">
          <ThemeSwitcher />
          <PaletteSwitcher />
          <p className="app-footer__note">
            <span className="app-footer__name">{t('app.name')}</span> · {t('app.footer.note')}
          </p>
        </div>
      </footer>
      <ToastViewport />
      <ScrollRestoration />
    </div>
  )
}
